import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import SubscriptionForm from '@/components/subscriptions/SubscriptionForm';
import { useSubscriptionDetail } from '@/hooks/useSubscription';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useSubscriptionForm } from '@/hooks/useSubscriptionForm';
import { useLanguage } from '@/hooks/useLanguage';

const EditSubscription = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const subscriptionId = id || '';
  const { subscription, isLoading, error } = useSubscriptionDetail(subscriptionId);
  const { user } = useAuth();
  const { handleSubmit, isSubmitting } = useSubscriptionForm(subscriptionId);
  const { translate } = useLanguage();
  
  useEffect(() => {
    if (subscription) {
      console.log("Loaded subscription data:", subscription);
      console.log("Invoice reference value:", subscription.invoice_reference);
    }
  }, [subscription]);
  
  const onFormSubmit = useCallback(async (data: any) => {
    if (!user) {
      toast.error(translate("subscriptions.authRequired"));
      return;
    }
    
    try {
      console.log("Form submission data:", data);
      console.log("Invoice reference in form data:", data.invoice_reference);
      
      // Make sure we don't try to update updated_by field
      if ('updated_by' in data) {
        delete data.updated_by;
      }
      
      const success = await handleSubmit(data);
      
      if (success) {
        toast.success(translate("subscriptions.updatedSuccessfully"));
        navigate(`/subscriptions/${subscriptionId}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(translate("subscriptions.error"));
    }
  }, [user, handleSubmit, subscriptionId, navigate, translate]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="mb-6">
          <Link to="/subscriptions" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="h-4 w-4 mr-1" />
            {translate('common.back')} {translate('common.to')} {translate('navigation.subscriptions')}
          </Link>
        </div>

        <div className="mb-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-48 mt-2" />
        </div>

        <div className="space-y-6">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-60 w-full" />
        </div>
      </MainLayout>
    );
  }

  if (error || !subscription) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">{translate('subscriptions.notFound')}</h2>
          <p className="text-muted-foreground mb-6">{error || translate('subscriptions.doesNotExist')}</p>
          <Link to="/subscriptions">
            <Button variant="outline">
              {translate('common.back')} {translate('common.to')} {translate('navigation.subscriptions')}
            </Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">{translate('subscriptions.authRequired')}</h2>
          <p className="text-muted-foreground mb-6">{translate('subscriptions.authRequired')}</p>
          <Link to="/auth">
            <Button>
              {translate('subscriptions.goToLogin')}
            </Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  // Only render the subscription form when we have all required data
  return (
    <MainLayout>
      <div className="mb-6">
        <Link to={`/subscriptions/${id}`} className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-4 w-4 mr-1" />
          {translate('common.back')} {translate('common.to')} {translate('subscriptions.subscription')}
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold">{translate('subscriptions.editSubscription')}</h1>
        <p className="text-muted-foreground">{translate('subscriptions.updateFor')} {subscription?.customer?.customer_name}</p>
      </div>

      {subscription && (
        <SubscriptionForm
          subscription={subscription}
          onSubmit={onFormSubmit}
          isLoading={isSubmitting}
          hideServices={true}
        />
      )}
    </MainLayout>
  );
};

export default EditSubscription;
